import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, KeyRound, ChevronDown } from "lucide-react";
import {
  NAV_ITEMS,
  SERVICE_LINKS,
  PHONE_DISPLAY,
  PHONE_RAW,
} from "../../data/content";

export const Navbar = ({ onNav }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setMobileServices(false);
    onNav(id);
  };

  const closeAll = () => {
    setOpen(false);
    setMobileServices(false);
    setServicesOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-brand-navy/95 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.35)] border-b border-white/5"
            : "bg-brand-navy2 shadow-[0_4px_20px_rgba(0,0,0,0.25)] border-b border-white/10"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => handleNav("hero")}
              data-testid="nav-logo"
              className="flex items-center gap-2.5 group"
            >
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-orange text-white shadow-lg shadow-brand-orange/25 group-hover:rotate-12 transition-transform duration-300">
                <KeyRound className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <span className="font-heading font-extrabold text-white text-lg tracking-tight leading-none">
                Klíče<span className="text-brand-orange">Ostrava</span>
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) =>
                item.id === "sluzby" ? (
                  <div
                    key="sluzby"
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      onClick={() => handleNav("sluzby")}
                      data-testid="nav-link-sluzby"
                      className="relative flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-slate-200 hover:text-white transition-colors group"
                    >
                      {item.label}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 ${
                          servicesOpen ? "rotate-180 text-brand-orange" : ""
                        }`}
                      />
                      <span className="absolute left-4 right-6 -bottom-0.5 h-0.5 bg-brand-orange scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                    </button>

                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                          data-testid="services-dropdown"
                          className="absolute left-0 top-full pt-3 w-64"
                        >
                          <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden p-2">
                            {SERVICE_LINKS.map((s) => {
                              const Icon = s.icon;
                              const slug = s.href.slice(1);
                              return (
                                <Link
                                  key={s.href}
                                  to={s.href}
                                  onClick={closeAll}
                                  data-testid={`dropdown-link-${slug}`}
                                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-brand-navy hover:bg-slate-50 group/link transition-colors"
                                >
                                  <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand-navy text-brand-orange group-hover/link:bg-brand-orange group-hover/link:text-white transition-colors">
                                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                                  </span>
                                  {s.label}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => handleNav(item.id)}
                    data-testid={`nav-link-${item.id}`}
                    className="relative px-4 py-2 text-sm font-semibold text-slate-200 hover:text-white transition-colors group"
                  >
                    {item.label}
                    <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-brand-orange scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </button>
                )
              )}
            </nav>

            {/* Right: single phone CTA */}
            <div className="hidden md:flex items-center">
              <a
                href={PHONE_RAW}
                data-testid="nav-call-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-brand-orange/25 hover:bg-brand-orangeDark hover:-translate-y-0.5 transition-all duration-300"
              >
                <Phone className="h-4 w-4" strokeWidth={2.6} />
                {PHONE_DISPLAY}
              </a>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              className="lg:hidden grid place-items-center h-11 w-11 rounded-lg bg-white/10 text-white backdrop-blur-sm"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 lg:hidden bg-brand-navy/98 backdrop-blur-lg pt-20 overflow-y-auto"
          >
            <nav className="flex flex-col px-6 py-6 gap-1">
              {NAV_ITEMS.map((item, i) =>
                item.id === "sluzby" ? (
                  <div key="sluzby" className="border-b border-white/10">
                    <button
                      onClick={() => setMobileServices((v) => !v)}
                      data-testid="mobile-services-toggle"
                      className="w-full flex items-center justify-between font-heading text-2xl font-bold text-white py-3"
                    >
                      {item.label}
                      <ChevronDown
                        className={`h-6 w-6 transition-transform duration-300 ${
                          mobileServices ? "rotate-180 text-brand-orange" : ""
                        }`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileServices && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-1 pb-4 pl-2">
                            {SERVICE_LINKS.map((s) => {
                              const slug = s.href.slice(1);
                              return (
                                <Link
                                  key={s.href}
                                  to={s.href}
                                  onClick={closeAll}
                                  data-testid={`mobile-dropdown-link-${slug}`}
                                  className="text-slate-200 text-lg font-semibold py-2 hover:text-brand-orange transition-colors"
                                >
                                  {s.label}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    onClick={() => handleNav(item.id)}
                    data-testid={`mobile-nav-link-${item.id}`}
                    className="text-left font-heading text-2xl font-bold text-white py-3 border-b border-white/10"
                  >
                    {item.label}
                  </motion.button>
                )
              )}
              <a
                href={PHONE_RAW}
                data-testid="mobile-call-btn"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-4 text-base font-bold text-white shadow-lg"
              >
                <Phone className="h-5 w-5" strokeWidth={2.6} />
                Zavolat: {PHONE_DISPLAY}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
